import * as Sentry from '@sentry/react'
import type {
  BrowserOptions,
  ErrorEvent,
} from '@sentry/react'
import type { Integration } from '@sentry/core'
import type {
  SentryReadyConfig,
  SentryUser,
  SentryBreadcrumbData,
} from './sentryTypes'

// Keys whose values must never be sent to Sentry. Compared case-insensitively.
const SENSITIVE_KEYS = [
  'authorization',
  'cookie',
  'x-api-key',
  'accesstoken',
  'refreshtoken',
  'token',
  'password',
  'secret',
  'access_token',
  'access-token',
  'refresh_token',
  'refresh-token',
]

const DEFAULT_IGNORE_ERRORS: Array<string | RegExp> = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
]

const DEFAULT_DENY_URLS: Array<string | RegExp> = [
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
  /^safari-web-extension:\/\//i,
]

const MAX_STRING_LENGTH = 1000

let isEnabled = false
let isInitialized = false

function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase()
  return SENSITIVE_KEYS.includes(normalized)
}

function redactRecord(input: Record<string, unknown>): Record<string, unknown> {
  const output: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(input)) {
    output[key] = isSensitiveKey(key) ? '[redacted]' : value
  }
  return output
}

export function redactDeep(value: unknown, seen: WeakSet<object> = new WeakSet()): unknown {
  if (value === null || typeof value !== 'object') {
    return value
  }
  if (seen.has(value)) {
    return '[circular]'
  }
  seen.add(value)

  if (Array.isArray(value)) {
    return value.map((item) => redactDeep(item, seen))
  }

  const output: Record<string, unknown> = {}
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    output[key] = isSensitiveKey(key) ? '[redacted]' : redactDeep(val, seen)
  }
  return output
}

// Redacts query-string values whose parameter name matches a sensitive key.
// Handles both absolute and relative URLs. Never throws.
function stripSensitiveQueryParams(url: string): string {
  try {
    const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(url)
    // Use a dummy base for relative URLs so URL() can parse them.
    const parsed = new URL(url, hasScheme ? undefined : 'http://placeholder.invalid')
    let mutated = false
    parsed.searchParams.forEach((_value, key) => {
      if (isSensitiveKey(key)) {
        parsed.searchParams.set(key, '[redacted]')
        mutated = true
      }
    })
    if (!mutated) {
      return url
    }
    if (hasScheme) {
      return parsed.toString()
    }
    // Reconstruct the relative form (path + search + hash) for relative inputs.
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return url
  }
}

// Mutates and returns the event in place. This is intentional and idiomatic
// for Sentry's beforeSend hook, which expects the callback to mutate+return
// the event (or return null to drop it). Scrubs sensitive data from:
// request headers, request data, query_string, cookies, request URL query
// params, extra context, contexts, and breadcrumb data/URLs — preventing
// tokens, passwords, and other secrets from reaching Sentry.
function sanitizeEvent(event: ErrorEvent): ErrorEvent {
  if (event.request) {
    if (event.request.headers) {
      event.request.headers = redactRecord(
        event.request.headers as Record<string, unknown>,
      ) as Record<string, string>
    }
    if (event.request.cookies) {
      event.request.cookies = '[redacted]' as unknown as typeof event.request.cookies
    }
    if (event.request.data) {
      event.request.data = redactDeep(event.request.data)
    }
    if (typeof event.request.query_string === 'string') {
      // Redact param values by name, preserving the raw query-string format.
      const params = new URLSearchParams(event.request.query_string)
      let mutated = false
      params.forEach((_value, key) => {
        if (isSensitiveKey(key)) {
          params.set(key, '[redacted]')
          mutated = true
        }
      })
      if (mutated) {
        event.request.query_string = params.toString()
      }
    } else if (event.request.query_string) {
      event.request.query_string = redactDeep(
        event.request.query_string,
      ) as typeof event.request.query_string
    }
    if (event.request.url) {
      event.request.url = stripSensitiveQueryParams(event.request.url)
    }
  }

  if (event.extra) {
    event.extra = redactDeep(event.extra) as Record<string, unknown>
  }

  if (event.contexts) {
    event.contexts = redactDeep(event.contexts) as typeof event.contexts
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
      if (!breadcrumb.data) {
        return breadcrumb
      }
      const data = redactDeep(breadcrumb.data) as Record<string, unknown>
      // Auto http breadcrumbs put the request URL under data.url.
      if (typeof data.url === 'string') {
        data.url = stripSensitiveQueryParams(data.url)
      }
      return { ...breadcrumb, data }
    })
  }

  return event
}

function makeBeforeSend(
  custom?: SentryReadyConfig['beforeSend'],
): NonNullable<BrowserOptions['beforeSend']> {
  return (event, hint) => {
    const sanitized = sanitizeEvent(event)
    if (custom) {
      return custom(sanitized, hint)
    }
    return sanitized
  }
}

export function initSentry(config: SentryReadyConfig): void {
  if (isInitialized) {
    return
  }

  const dsn = config.dsn
  const enabled = config.enabled ?? Boolean(dsn)

  // Mark as initialized regardless, so a missing DSN does not retry on every render.
  isInitialized = true
  isEnabled = enabled

  if (!enabled || !dsn) {
    return
  }

  const enableTracing = config.enableTracing ?? true
  const enableReplay = config.enableReplay ?? true

  const integrations: Integration[] = []
  if (enableTracing) {
    integrations.push(Sentry.browserTracingIntegration())
  }
  if (enableReplay) {
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    )
  }

  try {
    Sentry.init({
      dsn,
      environment: config.environment,
      release: config.release,
      integrations,
      tracesSampleRate: config.tracesSampleRate ?? 0.1,
      replaysSessionSampleRate: config.replaysSessionSampleRate ?? 0.01,
      replaysOnErrorSampleRate: config.replaysOnErrorSampleRate ?? 1.0,
      ignoreErrors: [...DEFAULT_IGNORE_ERRORS, ...(config.ignoreErrors ?? [])],
      denyUrls: [...DEFAULT_DENY_URLS, ...(config.denyUrls ?? [])],
      beforeSend: makeBeforeSend(config.beforeSend),
    })
  } catch {
    // Never let Sentry init crash the app.
    isEnabled = false
  }

}

export function setSentryUser(user: SentryUser): void {
  if (!isEnabled) return
  try {
    Sentry.setUser({
      ...user,
      id: user.id === undefined ? undefined : String(user.id),
    })
  } catch {
    // no-op
  }
}

export function clearSentryUser(): void {
  if (!isEnabled) return
  try {
    Sentry.setUser(null)
  } catch {
    // no-op
  }
}

export function setSentryContext(
  name: string,
  context: Record<string, unknown>,
): void {
  if (!isEnabled) return
  try {
    Sentry.setContext(name, redactDeep(context) as Record<string, unknown>)
  } catch {
    // no-op
  }
}

export function addSentryBreadcrumb(
  message: string,
  data?: SentryBreadcrumbData,
): void {
  if (!isEnabled) return
  try {
    Sentry.addBreadcrumb({
      message: message.slice(0, MAX_STRING_LENGTH),
      level: 'info',
      data: data ? redactDeep(data) as Record<string, unknown> : undefined,
    })
  } catch {
    // no-op
  }
}

export function captureHandledError(
  error: unknown,
  extra?: Record<string, unknown>,
): void {
  if (!isEnabled) return
  try {
    Sentry.captureException(error, {
      extra: extra ? redactDeep(extra) as Record<string, unknown> : undefined,
    })
  } catch {
    // no-op
  }
}

export function captureMessage(
  message: string,
  extra?: Record<string, unknown>,
): void {
  if (!isEnabled) return
  try {
    Sentry.captureMessage(message, {
      level: 'info',
      extra: extra ? redactDeep(extra) as Record<string, unknown> : undefined,
    })
  } catch {
    // no-op
  }
}
